<script lang="ts">
    import { addLabels, captureError } from '@otto-ec/global-resources/apm';
    import Esi from '@shared/components/esi/Esi.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import BenefitCinema from '@tilelist/components/benefit-cinema/BenefitCinema.svelte';
    import BenefitCinemaProvider from '@tilelist/components/benefit-cinema/BenefitCinemaProvider.svelte';
    import BenefitTeaser from '@tilelist/components/benefit-teaser/BenefitTeaser.svelte';
    import BottomPagingBar from '@tilelist/components/bottom-paging-bar/BottomPagingBar.svelte';
    import ExacTag from '@tilelist/components/exac-tag/ExacTag.svelte';
    import History from '@tilelist/components/history/History.svelte';
    import NavCategorySlider from '@tilelist/components/nav-category-slider/NavCategorySlider.svelte';
    import NavListHeader from '@tilelist/components/nav-list-header/NavListHeader.svelte';
    import OttoUpPromo from '@tilelist/components/otto-up-promo/OttoUpPromo.svelte';
    import PandaComparisonProvider from '@tilelist/components/product-comparison/PandaComparisonProvider.svelte';
    import ProductComparisonSheet from '@tilelist/components/product-comparison/ProductComparisonSheet.svelte';
    import RefinementBar from '@tilelist/components/refinement-bar/RefinementBar.svelte';
    import SdaHoarding from '@tilelist/components/sda-hoarding/SdaHoarding.svelte';
    import FallbackResult from '@tilelist/components/tile-list-page/FallbackResult.svelte';
    import ItemCount from '@tilelist/components/tile-list-page/ItemCount.svelte';
    import SearchResult from '@tilelist/components/tile-list-page/SearchResult.svelte';
    import TileList from '@tilelist/components/tile-list/TileList.svelte';
    import type { ProductItem, TileItem } from '@tilelist/domain/composition/TileListitem.ts';
    import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
    import {
        FOOTER_FEATURE_INDEX,
        getTopSlotSdaFeatureIndex,
    } from '@tilelist/domain/external-data-attributes/ExternalDataAttributes.ts';
    import { shouldShowWishlistIcon } from '@tilelist/domain/feature-visibility/WishlistElement.ts';

    let {
        tileListItems,
        comparisonId,
        page,
        displayCount,
        isFallback,
        rankedCount,
        sponsoredCount,
        pageItemCount,
        context,
        showTopInfos,
        cacheRestored,
        isApp,
        onRedirectToPdp,
        onProductItemUpdated,
    }: TileListPageLayout & {
        showTopInfos: boolean;
        cacheRestored: boolean;
        isApp: boolean;
        onRedirectToPdp: (item: TileItem) => void;
        onProductItemUpdated: (item: ProductItem) => void;
    } = $props();

    const onex = injectWiring().onex;

    const rule = $derived(context.rule);
    const assortment = $derived(context.searchContext?.topNImplicit?.assortmentNavigation?.[0]);
    const isSearch = $derived(!!context.searchContext?.searchTerm);
    const showOttoUpPromo = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_OTTO_UP_PROMO'));
    const includeHoardingInFooterSlot = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_WATO_HOARDING'));
    const showBenefitCinema = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_BENEFIT_CINEMA'));
    const showSdaHoarding = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_SDA_HOARDING'));
    const showBenefits = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_BENEFITS'));
    const showNavCategorySlider = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_NAV_CATEGORY_SLIDER'));
    const showNavListHeader = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_NAV_LIST_HEADER'));
    const showRefinementBar = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_REFINEMENT_BAR'));
    const showSponsoredTag = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_SPONSORED_TAG'));
    const showWishlistElement = $derived(shouldShowWishlistIcon(onex));
    const showPricingComponent = $derived(!onex.isExperimentGroup('PRICING_COMPONENT', 'E3892A'));
    const includeNavListHeader = $derived(onex.isExperimentGroup('ARRANGEMENT_LIST_ELEMENTS_ON_TOP', 'Positions'));
    const topSlotSdaFeatureIndex = $derived(getTopSlotSdaFeatureIndex(showBenefitCinema, showSdaHoarding));

    $effect(() => {
        if (isFallback) {
            captureError(new Error('Fallback'), { team: 'reptile', service: 'dundee' }, { type: 'fallback' });
        }
        addLabels({ rule: context.rule });
        addLabels({ items: tileListItems.length });
    });
</script>

{#snippet wishlistBundle()}
    {#if showWishlistElement}
        <Esi src="/static-proxy/feature-components/wishlist/main.html" />
    {/if}
{/snippet}

<History {rule} {page} />

<!-- NOTE: id=reptile-tilelist-bracket is still used by Darwin -->
<div
    id="reptile-tilelist-bracket"
    class={[
        'reptile-tilelist__bracket',
        {
            'reptile-tilelist__bracket--hidden': onex.isToggleActive('TILELIST_INITIALLY_VISIBLE')
                ? undefined
                : !cacheRestored,
        },
    ]}
    data-rule={rule}
    data-assortment={assortment}
>
    {#if isFallback}
        <FallbackResult>
            <TileList
                {context}
                {tileListItems}
                {cacheRestored}
                {showTopInfos}
                featureIndexOffset={0}
                {onRedirectToPdp}
                {onProductItemUpdated}
            />
            {@render wishlistBundle()}
        </FallbackResult>
    {:else}
        <BenefitCinemaProvider {rule} storeEnabled={showBenefitCinema}>
            {#if showBenefitCinema}
                <BenefitCinema dreson={rule} position="top" />
            {/if}

            {#if showSdaHoarding}
                <SdaHoarding
                    listSlot="top-slot"
                    searchContext={context.searchContext}
                    featureIndex={topSlotSdaFeatureIndex}
                />
            {/if}

            <ExacTag items={tileListItems} />

            {#if includeNavListHeader}
                {#if showNavListHeader}
                    <NavListHeader {rule} count={displayCount} />
                {/if}
            {:else}
                {#if !isSearch && showNavCategorySlider}
                    <NavCategorySlider {rule} type="top-categories" />
                {/if}

                {#if showRefinementBar}
                    <RefinementBar {rule} {page} />
                {/if}
            {/if}

            <SearchResult {page}>
                {#if !includeNavListHeader}
                    <ItemCount count={displayCount} />
                {/if}
                <PandaComparisonProvider {comparisonId}>
                    <TileList
                        {context}
                        {tileListItems}
                        {cacheRestored}
                        {showTopInfos}
                        featureIndexOffset={topSlotSdaFeatureIndex}
                        {onRedirectToPdp}
                        {onProductItemUpdated}
                    />
                    {#if showSponsoredTag}
                        <Esi src="/static-proxy/feature-components/sponsored-tag/main.html" />
                    {/if}

                    {#if showBenefits}
                        <Esi src="/benefit-tag/tag/assets" />
                    {/if}

                    {@render wishlistBundle()}

                    {#if showPricingComponent}
                        <Esi src="/static-proxy/feature-components/pricing/main.html" />
                    {/if}

                    <BottomPagingBar {rankedCount} {sponsoredCount} {pageItemCount} pageContext={page} {rule} />
                    <ProductComparisonSheet {rule} />
                </PandaComparisonProvider>
            </SearchResult>

            {#if !isSearch && showNavCategorySlider}
                <NavCategorySlider {rule} type="pl-category-slider" />
            {/if}
            {#if showBenefitCinema}
                <BenefitTeaser />
            {/if}
            {#if showOttoUpPromo && context.searchContext?.rule}
                <OttoUpPromo searchContext={context.searchContext} />
            {/if}
            {#if showSdaHoarding && includeHoardingInFooterSlot}
                <SdaHoarding
                    listSlot="bottom-slot"
                    searchContext={context.searchContext}
                    {isApp}
                    featureIndex={FOOTER_FEATURE_INDEX}
                />
            {/if}
        </BenefitCinemaProvider>
    {/if}
</div>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/mixins';

    .reptile-tilelist__bracket {
        display: flex;
        flex-wrap: wrap;
        max-width: 100%;

        &--hidden {
            visibility: hidden;

            /*                                                            */
            /*                                                               */
            /*                                                            */
            :global(.reptile-sda-hoarding) {
                visibility: visible;
            }
        }

        @include mixins.breakpoint-from-l {
            display: block;
        }
    }
</style>
